import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { ValidatorsProps } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import useChartForm from '../../hooks/use-form';
import {
  useLazyGetAccountsCategoriesQuery,
  useUpdateCategoryMutation,
} from '../../store/services/categories';
import { changePanelState } from '../../store/slices/categories';

import ROUTES from '../../../../routes';

const useEdit = () => {
  const {
    accounts: { typeOptions },
    categories: {
      categories,
      panelIsOpen,
      categoryValidation,
      selectedCategory,
      selectedCategoryIsLoading,
    },
    groups: { groups },
  } = useAppSelector((state) => state.charts);
  const { activePermissions } = useAppSelector((state) => state.root);

  const { categoryId, primaryAccountId, groupId } = useParams();

  const dispatch = useAppDispatch();

  const { name, group, control, reset, errors, handleSubmit } = useChartForm({
    groups,
    categories,
    panelIsOpen,
    schema: categoryValidation as ValidatorsProps,
  });

  const [getAccountsCategory] = useLazyGetAccountsCategoriesQuery();

  const navigate = useNavigate();

  const [update, { isLoading: requestInProgress, isSuccess: onChangeSuccess }] =
    useUpdateCategoryMutation();

  const onSubmit = handleSubmit((values) => {
    update({
      body: {
        name: values.name,
        type: values.type,
        active: values.active,
      },
      id: categoryId as string,
    });
  });

  const handleClose = useCallback(() => {
    dispatch(changePanelState(false));

    setTimeout(() => {
      navigate(
        generatePath(ROUTES.PRIMARY_ACCOUNTS, {
          categoryId: categoryId as string,
          groupId: groupId as string,
          primaryAccountId: primaryAccountId as string,
        }),
      );
    }, 100);
  }, [dispatch]);

  useEffect(() => {
    if (selectedCategory) {
      reset({
        name: selectedCategory.name,
        type: selectedCategory.type,
        active: selectedCategory.active,
      });
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (categoryId) {
      getAccountsCategory(categoryId);
    }
  }, [categoryId, dispatch]);

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      dispatch(changePanelState(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (onChangeSuccess) {
      handleClose();
    }
  }, [onChangeSuccess, handleClose]);

  return {
    typeOptions,
    requestInProgress,
    selectedCategoryIsLoading,
    activePermissions,
    name,
    group,
    control,
    errors,
    onSubmit,
    categoryId,
    panelIsOpen,
    handleClose,
  };
};

export default useEdit;
